// INSTRUCTIONS - Outside of subaccounts and allocations, do not use .validate, .setTouched, or .setIsRequired, .hasError from any fields
// - All validation outside of subaccounts and allocations is handled by Yup validation schema

import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { DealBookingType, OTCDealData } from '@src/types/OTC';
import {
  isFixedRateLoanDealFormDraft,
  isFloatingRateLoanDealFormDraft,
  isForwardDealFormDraft,
  isOptionDealFormDraft,
} from '@src/types/OTC/otcGuards';
import type { CommissionCalculationTypeEnum, OTCProductTemplateSecurity } from '@talos/kyoko';
import {
  AllocationValueTypeEnum,
  EMPTY_OBJECT,
  Field,
  NumericField,
  Unit,
  getTypedKeys,
  type DateOnly,
  type ISubaccount,
} from '@talos/kyoko';
import type { OMSReferenceDataState } from '../types';
import type { TouchableFieldKey } from './types';

import { streamingDataSlice } from '@src/providers/AppStateProvider/streamingDataSlice';
import type { LoanSideEnum, OptionTypeEnum, PayoffTypeEnum, PricingModeEnum, SideEnum } from '@talos/kyoko';
import { initializeFieldsForModifying } from './ManualDealSliceModifyUtils';
import {
  getInitialFormState,
  getInitialState,
  getInitialTouchState,
  handleSymbolChange,
  handleTemplateSymbolChange,
  mapAllocationsToField,
  updateNotionalAmountFromCalculation,
  validateAllocations,
} from './ManualDealSliceUtils';
import { mapBookingTypeToProductType } from './utils';

// Type definitions for grouped actions
type CommonFieldsUpdate = Partial<{
  symbol: string | undefined;
  templateSymbol: string | undefined;
  counterparty: string | undefined;
  market: string | undefined;
  marketAccount: string | undefined;
  allocationValueType: AllocationValueTypeEnum;
  // Common date fields - consistent across all deal types
  tradeDate: DateOnly | undefined;
  effectiveTime: DateOnly | undefined;
  settlementTime: string | undefined;
  expiryTime: string | undefined;
}>;

type ForwardFieldsUpdate = CommonFieldsUpdate &
  Partial<{
    side: SideEnum;
    unitNotional: string | undefined;
    quantity: string | undefined;
    quantityCurrency: string | undefined;
    notionalAmount: string | undefined;
    forwardPrice: string | undefined;
    pricingMode: PricingModeEnum;
    index: string | undefined;
    spread: string | undefined;
    commission: string | undefined;
    commissionCurrency: string | undefined;
    commissionCalculationType: CommissionCalculationTypeEnum | '' | undefined;
    initialMarginCurrency: string | undefined;
    initialMarginPercent: string | undefined;
    releaseMarginPercent: string | undefined;
    maintenanceMarginPercent: string | undefined;
    marginCallPercent: string | undefined;
    liquidationMarginPercent: string | undefined;
    // Notional calculation fields
    notionalValue: string | null;
    notionalValueWithCurrency: {
      value: string;
      currency: string;
    } | null;
    payoffType: PayoffTypeEnum;
  }>;

type LoanFieldsUpdate = CommonFieldsUpdate &
  Partial<{
    loanSide: LoanSideEnum;
    quantity: string | undefined;
    quantityCurrency: string | undefined;
    unitLoanAmount: string | undefined;
    rate: string | undefined;
    paymentPeriod: string | undefined;
    rateSource: string | undefined;
    loanOriginationFeeSpread: string | undefined;
    loanOriginationFee: string | undefined;
    minimumLTV: string | undefined;
    marginCallLTV: string | undefined;
    liquidationLTV: string | undefined;
    collateralReleaseLTV: string | undefined;
    initialCollateralAmount: string | undefined;
    initialCollateralPercent: string | undefined;
    pricingMode: PricingModeEnum;
    spread: string | undefined;
    calculatedRate: string | undefined;

    /** Client-only value used for display only */
    loanAmount: string | undefined;
  }>;

type OptionFieldsUpdate = CommonFieldsUpdate &
  Partial<{
    side: SideEnum;
    strikePrice: string | undefined;
    optionType: OptionTypeEnum;
    quantity: string | undefined;
    quantityCurrency: string | undefined;
    // Notional calculation fields
    notionalValue: string | null;
    notionalValueWithCurrency: {
      value: string;
      currency: string;
    } | null;
    premium: string | undefined;
    commission: string | undefined;
    commissionCurrency: string | undefined;
    initialMarginCurrency: string | undefined;
    initialMarginPercent: string | undefined;
    releaseMarginPercent: string | undefined;
    maintenanceMarginPercent: string | undefined;
    marginCallPercent: string | undefined;
    liquidationMarginPercent: string | undefined;
  }>;

type FloatingRateLoanFieldsUpdate = CommonFieldsUpdate &
  Partial<{
    loanSide: LoanSideEnum;
    commission: string | undefined;
    commissionCurrency: string | undefined;
    quantity: string | undefined;
    quantityCurrency: string | undefined;
    unitLoanAmount: string | undefined;
    paymentPeriod: string | undefined;
    spread: string | undefined;
    calculatedRate: string | undefined;
    loanOriginationFeeSpread: string | undefined;
    minimumLTV: string | undefined;
    marginCallLTV: string | undefined;
    liquidationLTV: string | undefined;
    collateralReleaseLTV: string | undefined;
    initialCollateralAmount: string | undefined;
    initialCollateralPercent: string | undefined;

    /** Client-only value used for display only */
    loanAmount: string | undefined;
  }>;

const manualDealSlice = createSlice({
  name: 'manualDeal',
  initialState: getInitialState(),
  reducers: {
    setReferenceData: (state, action: PayloadAction<OMSReferenceDataState>) => {
      state.referenceData = action.payload;
      // HACK: Inject Floating Rate Loan and Vanilla Option stubs in staging
      if (state.initialized) {
        // If we're modifying a deal, ensure both Template and Symbol fields are properly set after reference data is populated
        if (state.dealBeingModified) {
          const { deal } = state.dealBeingModified;
          handleTemplateSymbolChange(state, deal.TemplateSymbol, true);
          handleSymbolChange(state, deal.Symbol, true);
        }
      }
    },
    markReady: (state, action: PayloadAction) => {
      state.initialized = true;
      streamingDataSlice.endpoints.getCounterparties.initiate(EMPTY_OBJECT);
    },
    setBookingType: (state, action: PayloadAction<DealBookingType>) => {
      const bookingType = action.payload;
      if (state.form.bookingTypeField.value && action.payload !== state.form.bookingTypeField.value) {
        const previousBookingType = state.form.bookingTypeField.value;
        const sameHeaderProduct =
          mapBookingTypeToProductType(previousBookingType) === mapBookingTypeToProductType(bookingType);

        const preservedTemplateSymbol = sameHeaderProduct ? state.form.templateSymbolField.value : undefined;
        const preservedSymbol = sameHeaderProduct ? state.form.symbolField.value : undefined;
        state.form = getInitialFormState(bookingType);
        if (preservedTemplateSymbol) {
          state.form.templateSymbolField = state.form.templateSymbolField.updateValue(preservedTemplateSymbol);
        }
        if (preservedSymbol) {
          state.form.symbolField = state.form.symbolField.updateValue(preservedSymbol);
        }
        // Same product header (e.g. OTC forward ↔ option): restoring template/symbol values is not enough —
        // `getInitialFormState` drops type-specific defaults that `updateCommon` applies via `handleTemplateSymbolChange`
        // (forward `unitNotionalField`, floating loan `rateSourceField`, etc.).
        if (sameHeaderProduct && preservedTemplateSymbol?.Symbol) {
          handleTemplateSymbolChange(state, preservedTemplateSymbol.Symbol, false);
        }
        // Reset modifying state when changing booking type
        state.dealBeingModified = undefined;
      }
      state.touchState = getInitialTouchState();

      state.form.bookingTypeField = state.form.bookingTypeField.updateValue(bookingType);
    },
    setAdditionalDependencies: (state, action: PayloadAction<{ tradableSubAccounts: ISubaccount[] }>) => {
      state.additionalDependencies = action.payload;
    },

    addAllocation: (state, action: PayloadAction) => {
      const unit =
        state.form.allocationValueTypeField.value === AllocationValueTypeEnum.Percentage ? Unit.Percent : Unit.Decimal;
      state.form.allocations = [
        ...state.form.allocations,
        {
          subAccountField: new Field<string>({ name: 'Sub Account' }),
          valueField: new NumericField({
            name: 'Allocation amount',
            unit,
          }),
        },
      ];
    },
    removeAllocation: (state, action: PayloadAction<number>) => {
      state.form.allocations = state.form.allocations.filter((_, index) => index !== action.payload);
      validateAllocations(state);
    },
    updateAllocation: (state, action: PayloadAction<{ index: number; subAccount?: string; value?: string }>) => {
      const { index, subAccount, value } = action.payload;
      if (index >= 0 && index < state.form.allocations.length) {
        const allocation = state.form.allocations[index];
        if (subAccount !== undefined) {
          allocation.subAccountField = allocation.subAccountField.updateValue(subAccount);
        }
        if (value !== undefined) {
          allocation.valueField = allocation.valueField.updateValue(value);
        }
        validateAllocations(state);
      }
    },
    setAllocations: (state, action: PayloadAction<{ subAccount: string; value: string }[]>) => {
      state.form.allocations = mapAllocationsToField(action.payload, state.form.allocationValueTypeField.value, true);
    },
    setIsLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setIsSubmitting: (state, action: PayloadAction<boolean>) => {
      state.isSubmitting = action.payload;
    },
    resetState: (state, action: PayloadAction) => {
      const newState = getInitialState();
      newState.referenceData = state.referenceData;
      newState.additionalDependencies = state.additionalDependencies;
      newState.dealBeingModified = undefined; // Reset the modifying state for new bookings
      return newState;
    },
    clearFormFields: (state, action: PayloadAction) => {
      // Reset form fields but preserve dealBeingModified state
      state.form = getInitialFormState(state.form.bookingTypeField.value);
      state.touchState = getInitialTouchState();
      state.validationErrors = {};
      state.computedSettlementDate = undefined;
      state.computedExpiryDate = undefined;
      // Keep dealBeingModified unchanged for this action
    },
    resetAfterSubmit: (state, action: PayloadAction) => {
      const preservedBookingType = state.form.bookingTypeField.value ?? 'forward';
      const preservedTemplate = state.form.templateSymbolField.value;
      state.form = getInitialFormState(preservedBookingType);
      state.touchState = getInitialTouchState();
      state.validationErrors = {};
      state.computedSettlementDate = undefined;
      state.computedExpiryDate = undefined;
      state.dealBeingModified = undefined;
      if (preservedTemplate?.Symbol) {
        handleTemplateSymbolChange(state, preservedTemplate.Symbol, false);
      }
    },
    touchAll: (state, action: PayloadAction) => {
      getTypedKeys(state.touchState).forEach(key => {
        state.touchState[key] = true;
      });

      state.form.allocations = state.form.allocations.map(pair => ({
        subAccountField: pair.subAccountField.setTouched(true),
        valueField: pair.valueField.setTouched(true),
      }));

      validateAllocations(state);
    },
    clearTouched: (state, action: PayloadAction) => {
      getTypedKeys(state.touchState).forEach(key => {
        state.touchState[key] = false;
      });
    },
    touchField: (state, action: PayloadAction<TouchableFieldKey>) => {
      const fieldKey = action.payload;
      if (!state.touchState[fieldKey]) {
        state.touchState[fieldKey] = true;
      }
    },
    setValidationErrors: (state, action: PayloadAction<{ [key: string]: string }>) => {
      state.validationErrors = action.payload;
    },

    clearValidationErrors: state => {
      state.validationErrors = {};
    },
    setComputedSettlementDate: (state, action: PayloadAction<string | null | undefined>) => {
      state.computedSettlementDate = action.payload;
    },
    setComputedExpiryDate: (state, action: PayloadAction<string | null | undefined>) => {
      state.computedExpiryDate = action.payload;
    },

    modifyDeal: (
      state,
      action: PayloadAction<{
        deal: OTCDealData;
        productTemplate: OTCProductTemplateSecurity;
      }>
    ) => {
      // Reset form state
      state.form = getInitialFormState();
      state.touchState = getInitialTouchState();
      state.validationErrors = {};
      state.computedSettlementDate = undefined;
      state.computedExpiryDate = undefined;
      state.dealBeingModified = action.payload;
      initializeFieldsForModifying(state, action.payload.deal, action.payload.productTemplate);
      state.initialized = true;
    },

    updateCommon: (state, action: PayloadAction<CommonFieldsUpdate>) => {
      const updates = action.payload;

      if ('symbol' in updates) {
        handleSymbolChange(state, updates.symbol);
      }

      if ('templateSymbol' in updates) {
        handleTemplateSymbolChange(state, updates.templateSymbol);
      }

      if ('counterparty' in updates) {
        state.form.counterpartyField = state.form.counterpartyField.updateValue(updates.counterparty);
      }

      if ('market' in updates) {
        state.form.marketField = state.form.marketField.updateValue(updates.market);
      }

      if ('marketAccount' in updates) {
        state.form.marketAccountField = state.form.marketAccountField.updateValue(updates.marketAccount);
      }

      if ('allocationValueType' in updates) {
        state.form.allocationValueTypeField = state.form.allocationValueTypeField.updateValue(
          updates.allocationValueType
        );

        // Update allocation value fields to use the correct unit
        const unit = updates.allocationValueType === AllocationValueTypeEnum.Percentage ? Unit.Percent : Unit.Decimal;
        state.form.allocations = state.form.allocations.map(allocation => ({
          ...allocation,
          valueField: allocation.valueField.updateUnit(unit),
        }));
      }

      // Common date fields handling
      if ('tradeDate' in updates) {
        state.form.tradeDateField = state.form.tradeDateField.updateValue(updates.tradeDate ?? undefined);
      }

      if ('effectiveTime' in updates) {
        state.form.effectiveDateField = state.form.effectiveDateField.updateValue(updates.effectiveTime ?? undefined);
      }

      if ('settlementTime' in updates) {
        state.form.settlementDateField = state.form.settlementDateField.updateValue(updates.settlementTime);
        // Clear computed settlement date when manually set
        if (updates.settlementTime != null) {
          state.computedSettlementDate = undefined;
        }
      }

      if ('expiryTime' in updates) {
        state.form.expiryDateField = state.form.expiryDateField.updateValue(updates.expiryTime);
        // Clear computed expiry date when manually set
        if (updates.expiryTime != null) {
          state.computedExpiryDate = undefined;
        }
      }
    },

    updateForward: (state, action: PayloadAction<ForwardFieldsUpdate>) => {
      if (!isForwardDealFormDraft(state.form)) {
        return; // Only apply to forward forms
      }

      const updates = action.payload;

      if ('side' in updates) {
        state.form.sideField = state.form.sideField.updateValue(updates.side);
      }

      if ('unitNotional' in updates) {
        state.form.unitNotionalField = state.form.unitNotionalField.updateValue(updates.unitNotional);
      }

      if ('quantity' in updates) {
        state.form.quantityField = state.form.quantityField.updateValue(updates.quantity);
      }

      if ('quantityCurrency' in updates) {
        state.form.quantityCurrencyField = state.form.quantityCurrencyField.updateValue(updates.quantityCurrency);
      }

      if ('notionalAmount' in updates) {
        state.form.notionalAmountField = state.form.notionalAmountField.updateValue(updates.notionalAmount);
      }

      if ('forwardPrice' in updates) {
        state.form.forwardPriceField = state.form.forwardPriceField.updateValue(updates.forwardPrice);
      }

      if ('pricingMode' in updates) {
        state.form.pricingModeField = state.form.pricingModeField.updateValue(updates.pricingMode);
      }

      if ('index' in updates) {
        state.form.indexField = state.form.indexField.updateValue(updates.index);
      }

      if ('spread' in updates) {
        state.form.spreadField = state.form.spreadField.updateValue(updates.spread);
      }

      // Common date fields handling (consistent across all deal types)
      if ('tradeDate' in updates) {
        state.form.tradeDateField = state.form.tradeDateField.updateValue(updates.tradeDate ?? undefined);
      }

      if ('effectiveTime' in updates) {
        state.form.effectiveDateField = state.form.effectiveDateField.updateValue(updates.effectiveTime ?? undefined);
      }

      if ('settlementTime' in updates) {
        state.form.settlementDateField = state.form.settlementDateField.updateValue(updates.settlementTime);
        // Clear computed settlement date when manually set
        if (updates.settlementTime != null) {
          state.computedSettlementDate = undefined;
        }
      }

      if ('expiryTime' in updates) {
        state.form.expiryDateField = state.form.expiryDateField.updateValue(updates.expiryTime);
        // Clear computed expiry date when manually set
        if (updates.expiryTime != null) {
          state.computedExpiryDate = undefined;
        }
      }

      if ('commission' in updates) {
        state.form.commissionBpsField = state.form.commissionBpsField.updateValue(updates.commission);
      }

      if ('commissionCurrency' in updates) {
        state.form.commissionCurrencyField = state.form.commissionCurrencyField.updateValue(updates.commissionCurrency);
      }

      if ('commissionCalculationType' in updates) {
        state.form.commissionCalculationTypeField = state.form.commissionCalculationTypeField.updateValue(
          updates.commissionCalculationType
        );
      }

      if ('initialMarginCurrency' in updates) {
        state.form.initialMarginCurrencyField = state.form.initialMarginCurrencyField.updateValue(
          updates.initialMarginCurrency
        );
      }

      if ('initialMarginPercent' in updates) {
        state.form.initialMarginPercentField = state.form.initialMarginPercentField.updateValue(
          updates.initialMarginPercent
        );
      }

      if ('releaseMarginPercent' in updates) {
        state.form.releaseMarginPercentField = state.form.releaseMarginPercentField.updateValue(
          updates.releaseMarginPercent
        );
      }

      if ('maintenanceMarginPercent' in updates) {
        state.form.maintenanceMarginPercentField = state.form.maintenanceMarginPercentField.updateValue(
          updates.maintenanceMarginPercent
        );
      }

      if ('marginCallPercent' in updates) {
        state.form.marginCallPercentField = state.form.marginCallPercentField.updateValue(updates.marginCallPercent);
      }

      if ('liquidationMarginPercent' in updates) {
        state.form.liquidationMarginPercentField = state.form.liquidationMarginPercentField.updateValue(
          updates.liquidationMarginPercent
        );
      }

      // Handle notional calculation from calculation logic
      if ('notionalValue' in updates || 'notionalValueWithCurrency' in updates || 'payoffType' in updates) {
        const { notionalValue, notionalValueWithCurrency, payoffType } = updates;
        updateNotionalAmountFromCalculation(state, { notionalValue, notionalValueWithCurrency, payoffType });
      }
    },

    updateFixedRateLoan: (state, action: PayloadAction<LoanFieldsUpdate>) => {
      const updates = action.payload;

      if (!isFixedRateLoanDealFormDraft(state.form)) {
        return; // Only apply loan-specific fields to loan forms
      }

      if ('quantity' in updates) {
        state.form.quantityField = state.form.quantityField.updateValue(updates.quantity);
      }

      if ('quantityCurrency' in updates) {
        state.form.quantityCurrencyField = state.form.quantityCurrencyField.updateValue(updates.quantityCurrency);
      }

      if ('loanSide' in updates) {
        state.form.loanSideField = state.form.loanSideField.updateValue(updates.loanSide);
      }

      if ('unitLoanAmount' in updates) {
        state.form.unitLoanAmountField = state.form.unitLoanAmountField.updateValue(updates.unitLoanAmount);
      }

      if ('loanAmount' in updates) {
        state.form.loanAmountField = state.form.loanAmountField.updateValue(updates.loanAmount);
      }

      if ('rate' in updates) {
        state.form.rateField = state.form.rateField.updateValue(updates.rate);
      }

      if ('calculatedRate' in updates) {
        state.form.calculatedRateField = state.form.calculatedRateField.updateValue(updates.calculatedRate);
      }

      if ('paymentPeriod' in updates) {
        state.form.paymentPeriodField = state.form.paymentPeriodField.updateValue(updates.paymentPeriod);
      }

      if ('rateSource' in updates) {
        state.form.rateSourceField = state.form.rateSourceField.updateValue(updates.rateSource);
      }

      if ('loanOriginationFeeSpread' in updates) {
        state.form.loanOriginationFeeSpreadField = state.form.loanOriginationFeeSpreadField.updateValue(
          updates.loanOriginationFeeSpread
        );
      }

      if ('loanOriginationFee' in updates) {
        state.form.loanOriginationFeeField = state.form.loanOriginationFeeField.updateValue(updates.loanOriginationFee);
      }

      // Calculate loanOriginationFee when loanAmount or loanOriginationFeeSpread changes
      // loanOriginationFeeSpreadField.bigValue is already in decimal format (bpsValueSetter converts BPS input to decimal)
      if ('loanAmount' in updates || 'loanOriginationFeeSpread' in updates) {
        const loanAmount = state.form.loanAmountField.bigValue;
        const loanOriginationFeeSpread = state.form.loanOriginationFeeSpreadField.bigValue;
        const calculatedLoanOriginationFee =
          loanAmount != null && loanOriginationFeeSpread != null
            ? loanAmount.times(loanOriginationFeeSpread).toFixed()
            : undefined;
        state.form.loanOriginationFeeField =
          state.form.loanOriginationFeeField.updateValue(calculatedLoanOriginationFee);
      }

      // Common date fields handling (consistent across all deal types)
      if ('tradeDate' in updates) {
        state.form.tradeDateField = state.form.tradeDateField.updateValue(updates.tradeDate ?? undefined);
      }

      if ('effectiveTime' in updates) {
        state.form.effectiveDateField = state.form.effectiveDateField.updateValue(updates.effectiveTime ?? undefined);
      }

      if ('settlementTime' in updates) {
        state.form.settlementDateField = state.form.settlementDateField.updateValue(updates.settlementTime);
        // Clear computed settlement date when manually set
        if (updates.settlementTime != null) {
          state.computedSettlementDate = undefined;
        }
      }

      if ('expiryTime' in updates) {
        state.form.expiryDateField = state.form.expiryDateField.updateValue(updates.expiryTime);
        // Clear computed expiry date when manually set
        if (updates.expiryTime != null) {
          state.computedExpiryDate = undefined;
        }
      }

      if ('minimumLTV' in updates) {
        state.form.minimumLTVField = state.form.minimumLTVField.updateValue(updates.minimumLTV);
      }

      if ('marginCallLTV' in updates) {
        state.form.marginCallLTVField = state.form.marginCallLTVField.updateValue(updates.marginCallLTV);
      }

      if ('liquidationLTV' in updates) {
        state.form.liquidationLTVField = state.form.liquidationLTVField.updateValue(updates.liquidationLTV);
      }

      if ('collateralReleaseLTV' in updates) {
        state.form.collateralReleaseLTVField = state.form.collateralReleaseLTVField.updateValue(
          updates.collateralReleaseLTV
        );
      }

      if ('initialCollateralAmount' in updates) {
        state.form.initialCollateralAmountField = state.form.initialCollateralAmountField.updateValue(
          updates.initialCollateralAmount
        );
      }

      if ('initialCollateralPercent' in updates) {
        state.form.initialCollateralPercentField = state.form.initialCollateralPercentField.updateValue(
          updates.initialCollateralPercent
        );
      }

      if ('pricingMode' in updates) {
        state.form.pricingModeField = state.form.pricingModeField.updateValue(updates.pricingMode);
      }

      if ('spread' in updates) {
        state.form.spreadField = state.form.spreadField.updateValue(updates.spread);
      }
    },

    updateOption: (state, action: PayloadAction<OptionFieldsUpdate>) => {
      if (!isOptionDealFormDraft(state.form)) {
        return; // Only apply to option forms
      }

      const updates = action.payload;

      if ('side' in updates) {
        state.form.sideField = state.form.sideField.updateValue(updates.side);
      }

      if ('strikePrice' in updates) {
        state.form.strikePriceField = state.form.strikePriceField.updateValue(updates.strikePrice);
      }

      if ('optionType' in updates) {
        state.form.optionTypeField = state.form.optionTypeField.updateValue(updates.optionType);
      }

      if ('expiryTime' in updates) {
        state.form.expiryDateField = state.form.expiryDateField.updateValue(updates.expiryTime);
        // Clear computed expiry date when manually set
        if (updates.expiryTime != null) {
          state.computedExpiryDate = undefined;
        }
      }

      if ('quantity' in updates) {
        state.form.quantityField = state.form.quantityField.updateValue(updates.quantity);
      }

      if ('quantityCurrency' in updates) {
        state.form.quantityCurrencyField = state.form.quantityCurrencyField.updateValue(updates.quantityCurrency);
      }

      // Common date fields handling (consistent across all deal types)
      if ('tradeDate' in updates) {
        state.form.tradeDateField = state.form.tradeDateField.updateValue(updates.tradeDate ?? undefined);
      }

      if ('effectiveTime' in updates) {
        state.form.effectiveDateField = state.form.effectiveDateField.updateValue(updates.effectiveTime ?? undefined);
      }

      if ('settlementTime' in updates) {
        state.form.settlementDateField = state.form.settlementDateField.updateValue(updates.settlementTime);
        // Clear computed settlement date when manually set
        if (updates.settlementTime != null) {
          state.computedSettlementDate = undefined;
        }
      }

      if ('expiryTime' in updates) {
        state.form.expiryDateField = state.form.expiryDateField.updateValue(updates.expiryTime);
        // Clear computed expiry date when manually set
        if (updates.expiryTime != null) {
          state.computedExpiryDate = undefined;
        }
      }

      if ('commission' in updates) {
        state.form.commissionBpsField = state.form.commissionBpsField.updateValue(updates.commission);
      }

      if ('commissionCurrency' in updates) {
        state.form.commissionCurrencyField = state.form.commissionCurrencyField.updateValue(updates.commissionCurrency);
      }

      if ('premium' in updates) {
        state.form.premiumField = state.form.premiumField.updateValue(updates.premium);
      }

      if ('initialMarginCurrency' in updates) {
        state.form.initialMarginCurrencyField = state.form.initialMarginCurrencyField.updateValue(
          updates.initialMarginCurrency
        );
      }

      if ('initialMarginPercent' in updates) {
        state.form.initialMarginPercentField = state.form.initialMarginPercentField.updateValue(
          updates.initialMarginPercent
        );
      }

      if ('releaseMarginPercent' in updates) {
        state.form.releaseMarginPercentField = state.form.releaseMarginPercentField.updateValue(
          updates.releaseMarginPercent
        );
      }

      if ('maintenanceMarginPercent' in updates) {
        state.form.maintenanceMarginPercentField = state.form.maintenanceMarginPercentField.updateValue(
          updates.maintenanceMarginPercent
        );
      }

      if ('marginCallPercent' in updates) {
        state.form.marginCallPercentField = state.form.marginCallPercentField.updateValue(updates.marginCallPercent);
      }

      if ('liquidationMarginPercent' in updates) {
        state.form.liquidationMarginPercentField = state.form.liquidationMarginPercentField.updateValue(
          updates.liquidationMarginPercent
        );
      }

      // Handle notional calculation from calculation logic
      if ('notionalValue' in updates || 'notionalValueWithCurrency' in updates) {
        const { notionalValue, notionalValueWithCurrency } = updates;
        updateNotionalAmountFromCalculation(state, {
          notionalValue,
          notionalValueWithCurrency,
          payoffType: undefined, // Options don't have PayoffType, but function accepts it
        });
      }
    },

    updateFloatingRateLoan: (state, action: PayloadAction<FloatingRateLoanFieldsUpdate>) => {
      const updates = action.payload;

      if (!isFloatingRateLoanDealFormDraft(state.form)) {
        return; // Only apply floating rate loan-specific fields to floating rate loan forms
      }

      if ('quantity' in updates) {
        state.form.quantityField = state.form.quantityField.updateValue(updates.quantity);
      }

      if ('quantityCurrency' in updates) {
        state.form.quantityCurrencyField = state.form.quantityCurrencyField.updateValue(updates.quantityCurrency);
      }

      if ('loanSide' in updates) {
        state.form.loanSideField = state.form.loanSideField.updateValue(updates.loanSide);
      }

      if ('unitLoanAmount' in updates) {
        state.form.unitLoanAmountField = state.form.unitLoanAmountField.updateValue(updates.unitLoanAmount);
      }

      if ('loanAmount' in updates) {
        state.form.loanAmountField = state.form.loanAmountField.updateValue(updates.loanAmount);
      }

      if ('paymentPeriod' in updates) {
        state.form.paymentPeriodField = state.form.paymentPeriodField.updateValue(updates.paymentPeriod);
      }

      if ('calculatedRate' in updates) {
        state.form.calculatedRateField = state.form.calculatedRateField.updateValue(updates.calculatedRate);
      }

      if ('loanOriginationFeeSpread' in updates) {
        state.form.loanOriginationFeeSpreadField = state.form.loanOriginationFeeSpreadField.updateValue(
          updates.loanOriginationFeeSpread
        );
      }

      if ('spread' in updates) {
        state.form.spreadField = state.form.spreadField.updateValue(updates.spread);
      }

      // Calculate loanOriginationFee when loanAmount or loanOriginationFeeSpread changes
      // loanOriginationFeeSpreadField.bigValue is already in decimal format (bpsValueSetter converts BPS input to decimal)
      if ('loanAmount' in updates || 'loanOriginationFeeSpread' in updates) {
        const loanAmount = state.form.loanAmountField.bigValue;
        const loanOriginationFeeSpread = state.form.loanOriginationFeeSpreadField.bigValue;
        const calculatedLoanOriginationFee =
          loanAmount != null && loanOriginationFeeSpread != null
            ? loanAmount.times(loanOriginationFeeSpread).toFixed()
            : undefined;
        state.form.loanOriginationFeeField =
          state.form.loanOriginationFeeField.updateValue(calculatedLoanOriginationFee);
      }

      // Common date fields handling (consistent across all deal types)
      if ('tradeDate' in updates) {
        state.form.tradeDateField = state.form.tradeDateField.updateValue(updates.tradeDate ?? undefined);
      }

      if ('effectiveTime' in updates) {
        state.form.effectiveDateField = state.form.effectiveDateField.updateValue(updates.effectiveTime ?? undefined);
      }

      if ('settlementTime' in updates) {
        state.form.settlementDateField = state.form.settlementDateField.updateValue(updates.settlementTime);
        // Clear computed settlement date when manually set
        if (updates.settlementTime != null) {
          state.computedSettlementDate = undefined;
        }
      }

      if ('expiryTime' in updates) {
        state.form.expiryDateField = state.form.expiryDateField.updateValue(updates.expiryTime);
        // Clear computed expiry date when manually set
        if (updates.expiryTime != null) {
          state.computedExpiryDate = undefined;
        }
      }

      if ('minimumLTV' in updates) {
        state.form.minimumLTVField = state.form.minimumLTVField.updateValue(updates.minimumLTV);
      }

      if ('marginCallLTV' in updates) {
        state.form.marginCallLTVField = state.form.marginCallLTVField.updateValue(updates.marginCallLTV);
      }

      if ('liquidationLTV' in updates) {
        state.form.liquidationLTVField = state.form.liquidationLTVField.updateValue(updates.liquidationLTV);
      }

      if ('collateralReleaseLTV' in updates) {
        state.form.collateralReleaseLTVField = state.form.collateralReleaseLTVField.updateValue(
          updates.collateralReleaseLTV
        );
      }

      if ('initialCollateralAmount' in updates) {
        state.form.initialCollateralAmountField = state.form.initialCollateralAmountField.updateValue(
          updates.initialCollateralAmount
        );
      }

      if ('initialCollateralPercent' in updates) {
        state.form.initialCollateralPercentField = state.form.initialCollateralPercentField.updateValue(
          updates.initialCollateralPercent
        );
      }
    },
  },
});

export const {
  setReferenceData,
  markReady,
  setBookingType,
  setAdditionalDependencies,

  // form management
  setIsLoading,
  setIsSubmitting,
  resetState,
  clearFormFields,
  resetAfterSubmit,
  touchAll,
  clearTouched,
  touchField,
  setValidationErrors,
  clearValidationErrors,
  modifyDeal,

  // subaccount allocation management
  addAllocation,
  removeAllocation,
  updateAllocation,
  setAllocations,

  // grouped actions per deal type
  updateCommon,
  updateForward,
  updateFixedRateLoan,
  updateOption,
  updateFloatingRateLoan,
} = manualDealSlice.actions;

export const manualDealSliceActions = { ...manualDealSlice.actions };
export const manualDealSliceReducer = manualDealSlice.reducer;
